import { useId } from 'react';
import { delay, Glow, iconClassName, styles, svgProps } from '@/components/service-showcase/icons/shared';
import type { IServiceShowcaseSvgIconProps } from '@/components/service-showcase/icons/type';

// 60° tail sector. Sweep dir ≈ −30° → (269.7, 64). Trailing edge −90° → (190, 18).
// gradientUnits="objectBoundingBox" tracks SMIL rotation correctly (see earlier comments).
//   bbox: x=[190..269.7] y=[18..110]
//   (190, 18)   → (0,   0  ) ← trailing edge, transparent
//   (269.7, 64) → (1, 0.5)  ← sweep line endpoint, bright
const TAIL_PATH = 'M 190 110 L 269.7 64 A 92 92 0 0 0 190 18 Z';

// Blips timed to when the SMIL sweep crosses each point.
// SMIL begin="0.6s", period=8s. Flash is at 2% = 0.16s into radar-blip keyframe.
// animation-delay = t_hit − 0.16s
//   (252,  74): delta   0° → t_hit=0.60s → delay=0.44s
//   (236, 150): delta  71° → t_hit=2.18s → delay=2.02s
//   (142, 148): delta 172° → t_hit=4.42s → delay=4.26s
const BLIPS = [
  { cx: 252, cy: 74, r: 3, opacity: 1.0, d: 0.44 },
  { cx: 236, cy: 150, r: 2, opacity: 0.8, d: 2.02 },
  { cx: 142, cy: 148, r: 2.3, opacity: 0.6, d: 4.26 },
];

export default function RadarScanIcon(props: IServiceShowcaseSvgIconProps) {
  const { active = true, className, ...rest } = props;
  const id = useId();
  const gradId = `${id}g`;
  const glowId = `${id}f`;

  return (
    <svg {...svgProps} {...rest} className={iconClassName(active, className)}>
      <defs>
        {/* Aggressive taper: nearly all opacity lives in the last 15% near the leading edge */}
        <linearGradient id={gradId} x1='0' y1='0' x2='1' y2='0.5' gradientUnits='objectBoundingBox'>
          <stop offset='0%' stopColor='currentColor' stopOpacity='0' />
          <stop offset='40%' stopColor='currentColor' stopOpacity='0.03' />
          <stop offset='68%' stopColor='currentColor' stopOpacity='0.12' />
          <stop offset='85%' stopColor='currentColor' stopOpacity='0.28' />
          <stop offset='95%' stopColor='currentColor' stopOpacity='0.38' />
          <stop offset='100%' stopColor='currentColor' stopOpacity='0.45' />
        </linearGradient>

        {/* Bloom glow for the sweep line */}
        <filter id={glowId} x='-150%' y='-150%' width='400%' height='400%'>
          <feGaussianBlur stdDeviation='1.5' result='blur' />
          <feMerge>
            <feMergeNode in='blur' />
            <feMergeNode in='SourceGraphic' />
          </feMerge>
        </filter>
      </defs>

      <Glow cx={190} cy={110} r={20} opacity={0.3} />

      {[34, 64, 92].map((r, i) => (
        <circle
          key={r}
          cx='190'
          cy='110'
          r={r}
          stroke='currentColor'
          strokeOpacity={[0.35, 0.2, 0.12][i]}
          className={styles.ring}
          style={delay(`${0.15 + i * 0.18}s`)}
        />
      ))}

      {/* Outer group: CSS sweep-fadein controls visibility */}
      <g className={styles.sweep}>
        {/* Inner group: SMIL rotation */}
        <g>
          <animateTransform
            attributeName='transform'
            type='rotate'
            from='0 190 110'
            to='360 190 110'
            dur='8s'
            repeatCount='indefinite'
            begin='0.6s'
          />
          <path d={TAIL_PATH} fill={`url(#${gradId})`} />
          {/* Soft glow halo */}
          <line
            x1='190'
            y1='110'
            x2='252'
            y2='74'
            stroke='currentColor'
            strokeWidth='3'
            strokeOpacity='0.25'
            filter={`url(#${glowId})`}
          />
          {/* Crisp bright line on top */}
          <line
            x1='190'
            y1='110'
            x2='252'
            y2='74'
            stroke='currentColor'
            strokeWidth='1.3'
            strokeOpacity='0.88'
          />
        </g>
      </g>

      <circle cx='190' cy='110' r='4' fill='currentColor' className={styles.core} />

      {BLIPS.map(({ cx, cy, r, opacity, d }) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r={r}
          fill='currentColor'
          fillOpacity={opacity}
          className={styles.radarBlip}
          style={delay(`${d}s`)}
        />
      ))}
    </svg>
  );
}
